/**
 * Admin Portal → Partners → a partner → Contacts (partner launch UC1, workstream S5).
 *
 * People a partner sent us, who opted in to hear from Coach Cedar
 * (docs/plans/PARTNER_LAUNCH_PLAN_2026_10_17.md §1 UC1, §2 E5/E8, §3 O4):
 *
 *   - the funnel (lead tracking, S1 #5418): imported → emailed → (delivered, once the server counts
 *     it) → clicked → store tapped → installed → joined → redeemed → trial → paid → qualified (net
 *     of refunds), with reversed beside it — each stage's share measured against the earlier stage
 *     it is always part of, never over 100% — per tag or import, with each import's consent record
 *     and its route (CSV upload, partner API, webhook);
 *   - waiting, and why: the waiting contacts by what a Send would answer for each, in plain words
 *     (admin only — a verdict says whether an address holds an account);
 *   - new leads per month, by route (CSV, API, webhook), in UTC calendar months;
 *   - Import a list: CSV → preview (rows, rejects by line, repeats, never-email addresses,
 *     addresses that already have an account — counts only) → commit with the partner's consent
 *     attestation (how, where, which wording, and the confirmation checkbox);
 *   - Send: one tag (and optionally one import) and one variant — link + code, link only or code
 *     only. A test to the signed-in admin's own address unlocks Send for that same tag, import
 *     and variant; Send then asks for the count typed. A paused or ended tag or partner has no Send;
 *   - the list: masked (never a plain address), why each waiting contact waits, filters by state
 *     and link, resend for bounced / ambiguous contacts, erase with ERASE typed;
 *   - Withdrawals: the CSV to hand back to the partner.
 *
 * Read for every admin role; every write tool, and the withdrawals file (personal data the
 * partner receives), only for an admin or super admin (the server refuses the rest).
 *
 * Web-only (the admin SPA).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AdminButton, AdminCard } from '../../../components/admin/ui';
import { PartnersAdmin } from '../../../services/partnersAdminService';
import { tagEditable, type PartnerRecord, type TagRow } from './partnersLogic';
import { PartnerReviewQueue } from './PartnerReviewQueue';
import {
  CONTACTS_PAGE_SIZE,
  CONTACT_FUNNEL_LABELS,
  CONTACT_FUNNEL_TOOLTIPS,
  LEADS_COLUMNS,
  LEADS_COLUMN_LABELS,
  LINKED_VIA_LABELS,
  MAIL_STATES,
  MAIL_STATE_LABELS,
  SEND_VARIANTS,
  SEND_VARIANT_HINTS,
  SEND_VARIANT_LABELS,
  canErase,
  canResend,
  checkContactsFile,
  commitLabel,
  consentCheckboxLabel,
  consentErrors,
  contactEventText,
  contactFunnelDisplay,
  contactName,
  contactStageConversions,
  emptyConsentForm,
  importFormData,
  importResultText,
  importViaLabel,
  leadsByMonth,
  mailStateBadge,
  maskEmail,
  monthLabel,
  previewLines,
  previewRejectLines,
  sendBlockedReason,
  sendBody,
  sendResultText,
  sendVerdictLabel,
  testSendKey,
  waitingByVerdictText,
  type ConsentForm,
  type ContactRow,
  type ContactsFunnel,
  type ImportPreview,
  type MailState,
  type SendVariant,
} from './partnerContactsLogic';
import {
  NoticeLine,
  SAVE_FAILED,
  StatusBadge,
  TypedConfirm,
  errText,
  fmtDate,
  hint,
  input,
  label,
  row,
  saveBlob,
  td,
  th,
  useBusy,
  type Notice,
} from './PartnersShared';

type CsvFile = Blob & { name: string };

export const PartnerContactsPanel: React.FC<{
  partner: Pick<PartnerRecord, 'id' | 'display_name' | 'status' | 'code'>;
  tags: TagRow[];
  writable: boolean;
  /** The signed-in admin's own address — where a test send goes (null: unknown, so no test, so no send). */
  adminEmail: string | null;
  /** Whether /auth/me has answered (false: the role is still being read). */
  roleLoaded?: boolean;
}> = ({ partner, tags, writable, adminEmail, roleLoaded = true }) => {
  const partnerId = partner.id;
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, run] = useBusy();
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey((k) => k + 1);

  // ── the scope: the funnel, the list and Send all read it ──
  const [scopeTag, setScopeTag] = useState('');
  const [scopeImport, setScopeImport] = useState('');
  const scope = useMemo(() => ({ tagId: scopeTag || undefined, importId: scopeImport || undefined }), [scopeTag, scopeImport]);
  const scopeKey = `${scopeTag}|${scopeImport}`;

  // The funnel remembers the scope it was read for: Send never offers a count from another scope.
  const [loadedFunnel, setLoadedFunnel] = useState<{ key: string; data: ContactsFunnel } | null>(null);
  const [funnelError, setFunnelError] = useState<string | null>(null);
  useEffect(() => {
    // Every contacts route is admin-only (personal data): a support or viewer session reads nothing.
    if (!writable) return undefined;
    let live = true;
    setFunnelError(null);
    PartnersAdmin.contactsFunnel(partnerId, scope)
      .then((f) => { if (live) setLoadedFunnel({ key: `${scope.tagId ?? ''}|${scope.importId ?? ''}`, data: f }); })
      .catch((e) => { if (live) { setLoadedFunnel(null); setFunnelError(errText(e)); } });
    return () => { live = false; };
  }, [partnerId, scope, reloadKey, writable]);
  const funnel = loadedFunnel?.data ?? null;
  const funnelCurrent = !!loadedFunnel && loadedFunnel.key === scopeKey;
  const funnelStages = funnel?.funnel ?? {};
  const imports = funnel?.imports ?? [];
  const months = leadsByMonth(funnel?.leads_by_month);

  // ── the list ──
  const [mailState, setMailState] = useState<MailState | ''>('');
  const [linked, setLinked] = useState<'' | 'yes' | 'no'>('');
  const [offset, setOffset] = useState(0);
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [total, setTotal] = useState(0);
  const [listError, setListError] = useState<string | null>(null);
  useEffect(() => { setOffset(0); }, [scope, mailState, linked]);
  useEffect(() => {
    if (!writable) return undefined;
    let live = true;
    setListError(null);
    PartnersAdmin.listContacts(partnerId, { ...scope, mailState, linked, limit: CONTACTS_PAGE_SIZE, offset })
      .then((res) => { if (live) { setContacts(res.contacts || []); setTotal(Number(res.total || 0)); } })
      .catch((e) => { if (live) { setContacts([]); setTotal(0); setListError(errText(e)); } });
    return () => { live = false; };
  }, [partnerId, scope, mailState, linked, offset, reloadKey, writable]);

  const scopeImports = imports.filter((i) => !scopeTag || i.tag_id === scopeTag);
  const tagById = (id: string | null | undefined) => tags.find((t) => t.id === id) ?? null;

  // ── row actions ──
  const [resending, setResending] = useState<ContactRow | null>(null);
  const [erasing, setErasing] = useState<ContactRow | null>(null);

  const resend = () =>
    run(async () => {
      if (!resending) return;
      try {
        await PartnersAdmin.resendContact(partnerId, resending.id);
        setNotice({ kind: 'ok', text: `Invite queued again for ${contactName(resending)}.` });
        setResending(null);
        reload();
      } catch (e) {
        setResending(null);
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const erase = () =>
    run(async () => {
      if (!erasing) return;
      try {
        const res = await PartnersAdmin.eraseContact(partnerId, erasing.id);
        setNotice(res.already
          ? { kind: 'info', text: 'That contact was already erased: nothing more to remove.' }
          : { kind: 'ok', text: 'Erased: the contact’s personal data is gone; the address can never be emailed again.' });
        setErasing(null);
        reload();
      } catch (e) {
        setErasing(null);
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const downloadWithdrawals = () =>
    run(async () => {
      try {
        const { blob, filename } = await PartnersAdmin.withdrawalsCsv(partnerId);
        setNotice(saveBlob(blob, filename) ? { kind: 'ok', text: `Downloaded ${filename}.` } : { kind: 'error', text: SAVE_FAILED });
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const conv = contactStageConversions(funnelStages);
  const mailStates: Partial<Record<MailState, number>> = funnel?.mail_states ?? {};
  const stateLine = MAIL_STATES.filter((s) => Number(mailStates[s] || 0) > 0)
    .map((s) => `${MAIL_STATE_LABELS[s]} ${Number(mailStates[s]).toLocaleString('en-CA')}`)
    .join(' · ');
  const waitingLine = waitingByVerdictText(funnel?.waiting_by_verdict);

  // Every contacts route answers admins only (they carry personal data): no request, a plain note.
  if (!writable) {
    return (
      <AdminCard>
        <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Contacts</h4>
        <div style={hint} role="status">
          {roleLoaded
            ? 'Contacts are personal data: only an admin or super admin session can see or send them.'
            : 'Checking your admin role…'}
        </div>
      </AdminCard>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <NoticeLine notice={notice} onClose={() => setNotice(null)} />

      {/* ── the scope + the funnel ── */}
      <AdminCard>
        <div style={row}>
          <div>
            <label style={label} htmlFor="ct-scope-tag">Tag</label>
            <select id="ct-scope-tag" style={input} value={scopeTag}
              onChange={(e) => { setScopeTag(e.target.value); setScopeImport(''); }}>
              <option value="">All tags</option>
              {tags.map((t) => <option key={t.id} value={t.id}>{t.tag}</option>)}
            </select>
          </div>
          <div>
            <label style={label} htmlFor="ct-scope-import">Import</label>
            <select id="ct-scope-import" style={input} value={scopeImport} onChange={(e) => setScopeImport(e.target.value)}>
              <option value="">All imports</option>
              {scopeImports.map((i) => (
                <option key={i.id} value={i.id}>{`${fmtDate(i.created_at)} · ${i.tag} · ${importViaLabel(i.import_via)} · ${i.imported}`}</option>
              ))}
            </select>
          </div>
        </div>
        {funnelError && <NoticeLine notice={{ kind: 'error', text: funnelError }} />}
        {funnel && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 12, marginTop: 12 }}>
              {contactFunnelDisplay(funnelStages).map((s) => {
                const share = conv[s];
                return (
                  <div key={s} data-testid={`stage-${s}`} title={funnel.notes?.[s] ?? CONTACT_FUNNEL_TOOLTIPS[s]}>
                    <div style={hint}>{CONTACT_FUNNEL_LABELS[s]} ⓘ</div>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>{Number(funnelStages[s] || 0).toLocaleString('en-CA')}</div>
                    {share && (
                      <div style={hint}>{share.pct == null ? '—' : `${share.pct}% of ${CONTACT_FUNNEL_LABELS[share.base].toLowerCase()}`}</div>
                    )}
                  </div>
                );
              })}
              {/* Beside qualified, outside the chain: a bounty a refund has clawed back. */}
              <div data-testid="stage-reversed" title={funnel.notes?.reversed ?? CONTACT_FUNNEL_TOOLTIPS.reversed}>
                <div style={hint}>{CONTACT_FUNNEL_LABELS.reversed} ⓘ</div>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{Number(funnelStages.reversed || 0).toLocaleString('en-CA')}</div>
                <div style={hint}>refunded after qualifying</div>
              </div>
            </div>
            {stateLine && <div style={{ ...hint, marginTop: 8 }}>By email state: {stateLine}</div>}
            {waitingLine && <div style={{ ...hint, marginTop: 4 }} title={funnel.notes?.waiting_by_verdict}>{waitingLine}</div>}
          </>
        )}
      </AdminCard>

      {months.length > 0 && (
        <AdminCard flush>
          <div style={{ padding: '12px 12px 0' }}>
            <h4 style={{ margin: '0 0 4px', fontSize: 14 }}>New leads per month</h4>
            <div style={hint} title={funnel?.notes?.leads_by_month}>
              People added to the list each calendar month (UTC, the months statements use), by how they came in. Kept after the contacts are deleted. ⓘ
            </div>
          </div>
          <table data-testid="leads-by-month" style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
            <thead>
              <tr>
                <th style={th}>Month</th>
                {LEADS_COLUMNS.map((c) => <th key={c} style={th}>{LEADS_COLUMN_LABELS[c]}</th>)}
                <th style={th}>Total</th>
              </tr>
            </thead>
            <tbody>
              {months.map((m) => (
                <tr key={m.month}>
                  <td style={td}>{monthLabel(m.month)}</td>
                  {LEADS_COLUMNS.map((c) => <td key={c} style={td}>{m[c].toLocaleString('en-CA')}</td>)}
                  <td style={{ ...td, fontWeight: 600 }}>{m.total.toLocaleString('en-CA')}</td>
                </tr>
              ))}
              {months.length > 1 && (
                <tr style={{ fontWeight: 600 }}>
                  <td style={td}>All months</td>
                  {LEADS_COLUMNS.map((c) => (
                    <td key={c} style={td}>{months.reduce((a, m) => a + m[c], 0).toLocaleString('en-CA')}</td>
                  ))}
                  <td style={td}>{months.reduce((a, m) => a + m.total, 0).toLocaleString('en-CA')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </AdminCard>
      )}

      {imports.length > 0 && (
        <AdminCard flush>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Imported</th><th style={th}>Tag</th><th style={th}>Via</th><th style={th}>Contacts</th>
                <th style={th}>Emailed</th><th style={th}>Consent (how · where · wording)</th><th style={th}>By</th>
              </tr>
            </thead>
            <tbody>
              {imports.map((i) => (
                <tr key={i.id} data-testid="import-row">
                  <td style={td}>{fmtDate(i.created_at)}</td>
                  <td style={td}>{i.tag}</td>
                  <td style={td}>{importViaLabel(i.import_via)}</td>
                  <td style={td}>
                    {Number(i.imported || 0).toLocaleString('en-CA')}
                    {Number(i.updated || 0) > 0 && <div style={hint}>{`+${Number(i.updated).toLocaleString('en-CA')} refreshed`}</div>}
                  </td>
                  <td style={td}>{Number(i.emailed || 0).toLocaleString('en-CA')}</td>
                  <td style={td}>
                    {[i.consent_method, i.consent_source, i.consent_wording_version].filter(Boolean).join(' · ') || '—'}
                    {i.consent_attested_at && <div style={hint}>attested {fmtDate(i.consent_attested_at)}</div>}
                  </td>
                  <td style={td}>{i.created_by ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminCard>
      )}

      {writable && (
        <ImportCard partnerId={partnerId} partnerName={partner.display_name} partnerStatus={partner.status} tags={tags}
          onImported={(res, tagId) => {
            setNotice({ kind: 'ok', text: importResultText(res, tagById(tagId)?.tag ?? 'the tag') });
            setScopeTag(tagId);
            setScopeImport(res.id);
            reload();
          }} />
      )}

      {/* Who is waiting to be sent, each with their own drafted email (partner webhook intake). */}
      <PartnerReviewQueue partnerId={partnerId} tags={tags} partnerStatus={partner.status} tagId={scopeTag || undefined}
        importId={scopeImport || undefined} reloadKey={reloadKey} onSent={reload} />

      {writable && (
        <SendCard
          partnerId={partnerId}
          partnerName={partner.display_name}
          partnerStatus={partner.status}
          tag={tagById(scopeTag)}
          importId={scopeImport || undefined}
          readyToSend={funnel && funnelCurrent ? Number(funnel.ready_to_send || 0) : null}
          adminEmail={adminEmail}
          onSent={(text) => { setNotice({ kind: 'ok', text }); reload(); }}
          onError={(text) => setNotice({ kind: 'error', text })}
        />
      )}

      {/* ── the list ── */}
      <AdminCard>
        <div style={{ ...row, marginBottom: 8 }}>
          <div>
            <label style={label} htmlFor="ct-state">Email state</label>
            <select id="ct-state" style={input} value={mailState} onChange={(e) => setMailState(e.target.value as MailState | '')}>
              <option value="">Any</option>
              {MAIL_STATES.map((s) => <option key={s} value={s}>{MAIL_STATE_LABELS[s]}</option>)}
            </select>
          </div>
          <div>
            <label style={label} htmlFor="ct-linked">Account</label>
            <select id="ct-linked" style={input} value={linked} onChange={(e) => setLinked(e.target.value as '' | 'yes' | 'no')}>
              <option value="">Any</option>
              <option value="yes">Joined (linked)</option>
              <option value="no">Not joined yet</option>
            </select>
          </div>
          <div style={hint}>Addresses are masked. There is no search by name or address.</div>
        </div>
        {listError && <NoticeLine notice={{ kind: 'error', text: listError }} />}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Name</th><th style={th}>Email</th><th style={th}>Event</th><th style={th}>Tag</th>
                <th style={th}>Email state</th><th style={th}>Emailed</th><th style={th}>Clicked</th><th style={th}>Joined</th>
                {writable && <th style={th} />}
              </tr>
            </thead>
            <tbody>
              {contacts.length === 0 && <tr><td style={td} colSpan={writable ? 9 : 8}>No contacts here.</td></tr>}
              {contacts.map((c) => (
                <tr key={c.id} data-testid="contact-row">
                  <td style={td}>{contactName(c)}</td>
                  <td style={td}><code>{maskEmail(c.email_masked)}</code></td>
                  <td style={td}>{contactEventText(c)}</td>
                  <td style={td}>{c.tag}</td>
                  <td style={td}>
                    <StatusBadge status={MAIL_STATE_LABELS[c.mail_state] ?? c.mail_state} tone={mailStateBadge(c.mail_state)} />
                    {/* Why a waiting contact waits: what a Send would answer for them now (admin only). */}
                    {c.send_verdict && <div style={hint}>{sendVerdictLabel(c.send_verdict)}</div>}
                  </td>
                  <td style={td}>
                    {fmtDate(c.first_emailed_at)}
                    {c.reminder_sent_at && <div style={hint}>reminder {fmtDate(c.reminder_sent_at)}</div>}
                  </td>
                  <td style={td}>{fmtDate(c.first_clicked_at)}</td>
                  <td style={td}>{c.linked_via ? `${LINKED_VIA_LABELS[c.linked_via] ?? c.linked_via} · ${fmtDate(c.linked_at)}` : '—'}</td>
                  {writable && (
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {canResend(c) && <AdminButton size="sm" variant="ghost" onClick={() => setResending(c)}>Resend</AdminButton>}
                      {canErase(c) && <AdminButton size="sm" variant="ghost" onClick={() => setErasing(c)}>Erase</AdminButton>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {total > CONTACTS_PAGE_SIZE && (
          <div style={{ ...row, marginTop: 8, alignItems: 'center' }}>
            <AdminButton size="sm" variant="secondary" disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - CONTACTS_PAGE_SIZE))}>Previous</AdminButton>
            <span style={hint}>{`${offset + 1}–${Math.min(total, offset + CONTACTS_PAGE_SIZE)} of ${total.toLocaleString('en-CA')}`}</span>
            <AdminButton size="sm" variant="secondary" disabled={offset + CONTACTS_PAGE_SIZE >= total}
              onClick={() => setOffset(offset + CONTACTS_PAGE_SIZE)}>Next</AdminButton>
          </div>
        )}
      </AdminCard>

      {writable && (
        <AdminCard>
          <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Withdrawals</h4>
          <div style={hint}>
            People who unsubscribed or were removed. Hand this file back to {partner.display_name} so their list drops them
            too — unsubscribing from Coach Cedar&apos;s invite stops every partner invite, and the partner must honour it as well.
          </div>
          <div style={{ marginTop: 8 }}>
            <AdminButton size="sm" variant="secondary" onClick={downloadWithdrawals} disabled={busy}>Download withdrawals (CSV)</AdminButton>
          </div>
        </AdminCard>
      )}

      {resending && (
        <TypedConfirm
          title={`Resend the invite to ${contactName(resending)}?`}
          body={<>Their last send {resending.mail_state === 'bounced' ? 'bounced' : 'was never confirmed (it may or may not have arrived)'}. One more invite goes to {maskEmail(resending.email_masked)}.</>}
          expected="RESEND"
          confirmLabel="Resend invite"
          danger={false}
          busy={busy}
          onConfirm={resend}
          onCancel={() => setResending(null)}
        />
      )}
      {erasing && (
        <TypedConfirm
          title={`Erase ${contactName(erasing)}?`}
          body={<>Removes this contact&apos;s personal data now and keeps only a one-way &quot;never email&quot; record, so the address is never emailed again. This can&apos;t be undone.</>}
          expected="ERASE"
          confirmLabel="Erase contact"
          busy={busy}
          onConfirm={erase}
          onCancel={() => setErasing(null)}
        />
      )}
    </div>
  );
};

// ── import a list ────────────────────────────────────────────────────────────

const ImportCard: React.FC<{
  partnerId: string;
  partnerName: string;
  partnerStatus: PartnerRecord['status'];
  tags: TagRow[];
  onImported: (res: { id: string; imported: number; updated?: number }, tagId: string) => void;
}> = ({ partnerId, partnerName, partnerStatus, tags, onImported }) => {
  const importable = tags.filter((t) => tagEditable(t.status, partnerStatus));
  const [tagId, setTagId] = useState(importable[0]?.id ?? '');
  const [file, setFile] = useState<CsvFile | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [consent, setConsent] = useState<ConsentForm>(emptyConsentForm());
  const [error, setError] = useState<string | null>(null);
  const [busy, run] = useBusy();
  const chosenTag = importable.find((t) => t.id === tagId) ? tagId : importable[0]?.id ?? '';
  const consentProblems = consentErrors(consent);
  const fileProblem = checkContactsFile(file);

  // A different file or tag makes an earlier preview stale.
  const pickFile = useCallback((f: CsvFile | null) => { setFile(f); setPreview(null); setError(null); }, []);
  const pickTag = (id: string) => { setTagId(id); setPreview(null); setError(null); };

  const check = () =>
    run(async () => {
      if (fileProblem || !file) { setError(fileProblem); return; }
      setError(null);
      try {
        setPreview(await PartnersAdmin.previewContactImport(partnerId, importFormData({ blob: file, name: file.name }, chosenTag)));
      } catch (e) {
        setPreview(null);
        setError(errText(e));
      }
    });

  const commit = () =>
    run(async () => {
      if (!file || !preview || consentProblems.length) return;
      try {
        const res = await PartnersAdmin.commitContactImport(
          partnerId, importFormData({ blob: file, name: file.name }, chosenTag, { consent, previewSha256: preview.file_sha256 }),
        );
        onImported(res.import, chosenTag);
        setFile(null);
        setPreview(null);
        setConsent(emptyConsentForm());   // the attestation is per import (plan §3 O4)
      } catch (e) {
        setError(errText(e));
      }
    });

  const setC = (k: keyof ConsentForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setConsent((c) => ({ ...c, [k]: k === 'attested' ? !!(e.target as HTMLInputElement).checked : e.target.value }));

  if (!importable.length) {
    return (
      <AdminCard>
        <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Import a list</h4>
        <div style={hint}>{partnerStatus === 'ended' ? 'This partner has ended: no list can be imported.' : 'Make a tag first (Links & tags): every list belongs to a tag.'}</div>
      </AdminCard>
    );
  }

  return (
    <AdminCard>
      <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Import a list</h4>
      <div style={hint}>
        A CSV with <code>email</code> and <code>first_name</code> columns (required); <code>last_name</code>,{' '}
        <code>event_name</code>, <code>event_date</code> and <code>race</code> are optional, and any other column is kept as
        an extra. Only people who agreed to hear from Coach Cedar. Checking the file stores nothing.
      </div>
      <div style={{ ...row, marginTop: 8 }}>
        <div>
          <label style={label} htmlFor="ct-import-tag">Tag</label>
          <select id="ct-import-tag" style={input} value={chosenTag} onChange={(e) => pickTag(e.target.value)}>
            {importable.map((t) => <option key={t.id} value={t.id}>{t.tag}</option>)}
          </select>
        </div>
        <div>
          <label style={label} htmlFor="ct-file">CSV file</label>
          <input id="ct-file" type="file" accept=".csv,text/csv"
            onChange={(e) => pickFile((e.target.files && e.target.files[0]) ? (e.target.files[0] as CsvFile) : null)} />
        </div>
        <AdminButton size="sm" variant="secondary" onClick={check} disabled={busy || !file}>Check the file</AdminButton>
      </div>
      {error && <NoticeLine notice={{ kind: 'error', text: error }} />}

      {preview && (
        <div style={{ marginTop: 12 }} data-testid="contacts-preview">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 6, fontSize: 13 }}>
            {previewLines(preview).map((l) => (
              <div key={l.label} style={l.tone === 'warn' ? { color: 'var(--status-warning-text, #92400e)' } : undefined}>
                {l.label}: <b>{l.value}</b>
              </div>
            ))}
          </div>
          {preview.rejects_total > 0 && (
            <ul style={{ margin: '8px 0 0 16px', padding: 0, fontSize: 12 }}>
              {previewRejectLines(preview).map((l) => <li key={l}>{l}</li>)}
            </ul>
          )}
          <div style={{ ...hint, marginTop: 8 }}>
            Counts only — nobody is ever shown here by name or address. Addresses that already have a Coach Cedar account are
            stored but never emailed: Send skips them (an existing account is never credited to the partner by an email match).
          </div>

          <h4 style={{ margin: '16px 0 8px', fontSize: 14 }}>Consent (recorded with this import)</h4>
          <div style={row}>
            <div>
              <label style={label} htmlFor="ct-consent-method">How they agreed</label>
              <input id="ct-consent-method" style={{ ...input, width: 260 }} value={consent.method} onChange={setC('method')}
                placeholder="Checkbox on the registration form" />
            </div>
            <div>
              <label style={label} htmlFor="ct-consent-source">Where</label>
              <input id="ct-consent-source" style={{ ...input, width: 260 }} value={consent.source} onChange={setC('source')}
                placeholder="Race Weekend 2026 registration" />
            </div>
            <div>
              <label style={label} htmlFor="ct-consent-version">Consent wording version</label>
              <input id="ct-consent-version" style={{ ...input, width: 140 }} value={consent.wordingVersion} onChange={setC('wordingVersion')}
                placeholder="2026-10 v1" />
            </div>
          </div>
          <div style={{ marginTop: 8 }}>
            <label style={label} htmlFor="ct-consent-wording">The consent text they agreed to (optional, stored with this import)</label>
            <textarea id="ct-consent-wording" rows={2} style={{ ...input, width: '100%' }} value={consent.wording} onChange={setC('wording')}
              placeholder="Yes, Coach Cedar may email me about training with Run Ottawa." />
          </div>
          <label style={{ display: 'block', fontSize: 13, marginTop: 10 }}>
            <input id="ct-consent-attested" type="checkbox" checked={consent.attested} onChange={setC('attested')} />{' '}
            {consentCheckboxLabel(partnerName)}
          </label>
          <div style={{ ...row, marginTop: 10 }}>
            <AdminButton size="sm" onClick={commit}
              disabled={busy || !(Number(preview.importable || 0) + Number(preview.already_on_list || 0)) || consentProblems.length > 0}>
              {commitLabel(preview)}
            </AdminButton>
            {consentProblems.length > 0 && <span style={hint}>{consentProblems[0]}</span>}
          </div>
        </div>
      )}
    </AdminCard>
  );
};

// ── send ─────────────────────────────────────────────────────────────────────

const SendCard: React.FC<{
  partnerId: string;
  partnerName: string;
  partnerStatus: PartnerRecord['status'];
  tag: TagRow | null;
  importId: string | undefined;
  readyToSend: number | null;
  adminEmail: string | null;
  onSent: (text: string) => void;
  onError: (text: string) => void;
}> = ({ partnerId, partnerName, partnerStatus, tag, importId, readyToSend, adminEmail, onSent, onError }) => {
  const [variant, setVariant] = useState<SendVariant>('link_and_code');
  const [tested, setTested] = useState<Set<string>>(() => new Set());
  const [confirming, setConfirming] = useState(false);
  const [busy, run] = useBusy();

  if (!tag) {
    return (
      <AdminCard>
        <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Send</h4>
        <div style={hint}>Pick a tag to send (above): each send is for one tag, and optionally one import.</div>
      </AdminCard>
    );
  }

  const scope = { tagId: tag.id, importId };
  const blocked = sendBlockedReason(tag.status, partnerStatus);
  const key = testSendKey(scope, variant);
  const count = readyToSend ?? 0;

  const sendTest = () =>
    run(async () => {
      if (!adminEmail) return;
      try {
        const res = await PartnersAdmin.sendContacts(partnerId, sendBody(scope, variant, adminEmail));
        setTested((s) => new Set(s).add(key));
        onSent(sendResultText({ ...res, test: true, sent_to: res.sent_to ?? adminEmail }));
      } catch (e) {
        onError(errText(e));
      }
    });

  const sendAll = () =>
    run(async () => {
      try {
        const res = await PartnersAdmin.sendContacts(partnerId, sendBody(scope, variant));
        setConfirming(false);
        onSent(sendResultText(res));
      } catch (e) {
        setConfirming(false);
        onError(errText(e));
      }
    });

  return (
    <AdminCard>
      <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Send — {tag.tag}{importId ? ' (this import)' : ''}</h4>
      {blocked ? (
        <div style={{ ...hint, color: 'var(--status-warning-text, #92400e)' }}>{blocked}</div>
      ) : (
        <>
          <div style={hint}>
            Each person gets ONE invite from Coach Cedar naming {partnerName}, and one reminder on day 5 if they have not
            clicked or redeemed — never more. Unsubscribed addresses are skipped.
          </div>
          <div role="radiogroup" aria-label="What the email carries" style={{ ...row, marginTop: 8 }}>
            {SEND_VARIANTS.map((v) => (
              <label key={v} style={{ fontSize: 13 }} title={SEND_VARIANT_HINTS[v]}>
                <input id={`ct-variant-${v}`} type="radio" name="ct-variant" checked={variant === v} onChange={() => setVariant(v)} />{' '}
                {SEND_VARIANT_LABELS[v]}
              </label>
            ))}
          </div>
          <div style={hint}>{SEND_VARIANT_HINTS[variant]}</div>
          <div style={{ ...row, marginTop: 10, alignItems: 'center' }}>
            <AdminButton size="sm" variant="secondary" onClick={sendTest} disabled={busy || !adminEmail}>
              {`Send me a test${adminEmail ? ` (${adminEmail})` : ''}`}
            </AdminButton>
            <AdminButton size="sm" onClick={() => setConfirming(true)} disabled={busy || !tested.has(key) || count < 1}>
              {`Send to ${count.toLocaleString('en-CA')} ${count === 1 ? 'person' : 'people'}`}
            </AdminButton>
          </div>
          {!adminEmail && <div style={hint}>Couldn&apos;t read your admin address — reload the page to send yourself a test.</div>}
          {adminEmail && !tested.has(key) && <div style={hint}>Send yourself a test of this exact email first ({SEND_VARIANT_LABELS[variant]}).</div>}
          {tested.has(key) && count < 1 && <div style={hint}>Nobody here is waiting for an invite.</div>}
        </>
      )}

      {confirming && (
        <TypedConfirm
          title={`Email ${count.toLocaleString('en-CA')} ${count === 1 ? 'person' : 'people'} from ${partnerName}?`}
          body={<>Each gets one invite from Coach Cedar ({SEND_VARIANT_LABELS[variant].toLowerCase()}) for the {tag.tag} tag. Emails can&apos;t be unsent.</>}
          expected={String(count)}
          confirmLabel={`Send ${count.toLocaleString('en-CA')} ${count === 1 ? 'email' : 'emails'}`}
          busy={busy}
          onConfirm={sendAll}
          onCancel={() => setConfirming(false)}
        />
      )}
    </AdminCard>
  );
};

export default PartnerContactsPanel;
